import { lazy, Suspense, useEffect } from 'react';
import { DevTools } from './components/DevTools';
import { Hud } from './components/Hud';
import { Navigation } from './components/Navigation';
import { PwaUpdatePrompt } from './components/PwaUpdatePrompt';
import { zhCN } from './core/i18n/zhCN';
import { FarmPanel } from './features/farm/ui/FarmPanel';
import { HomePanel } from './features/home/ui/HomePanel';
import { QuestPage } from './features/quest/ui/QuestPage';
import { ShopPage } from './features/shop/ui/ShopPage';
import { AudioSettingsPanel } from './features/settings/ui/AudioSettingsPanel';
import { TutorialOverlay } from './features/tutorial/ui/TutorialOverlay';
import { WardrobePanel } from './features/wardrobe/ui/WardrobePanel';
import { installAudioUnlock } from './infrastructure/audio/audioEngine';
import { hydrateGame, installAutosave, saveNow, useGameStore } from './store/gameStore';
import { useSettingsStore } from './store/settingsStore';
import { useUiStore } from './store/uiStore';

const PhaserGame = lazy(async () => {
  const module = await import('./game/PhaserGame');
  return { default: module.PhaserGame };
});

export default function App() {
  const status = useGameStore((state) => state.status);
  const error = useGameStore((state) => state.loadError);
  const view = useUiStore((state) => state.view);
  const notice = useUiStore((state) => state.notice);

  useEffect(() => {
    void hydrateGame();
    void useSettingsStore.getState().actions.hydrate();
    const unsubscribe = installAutosave();
    const removeAudioUnlock = installAudioUnlock();
    const persist = () => void saveNow();
    document.addEventListener('visibilitychange', persist);
    window.addEventListener('pagehide', persist);
    return () => {
      unsubscribe();
      removeAudioUnlock();
      document.removeEventListener('visibilitychange', persist);
      window.removeEventListener('pagehide', persist);
    };
  }, []);

  if (status === 'loading')
    return (
      <main className="loading-screen">
        <span>🌱</span>
        <h1>{zhCN.app.loading}</h1>
      </main>
    );
  if (status === 'error')
    return (
      <main className="loading-screen error">
        <span>🍂</span>
        <h1>{zhCN.app.loadFailed}</h1>
        <p>{error}</p>
        <button onClick={() => void hydrateGame()}>{zhCN.app.retry}</button>
      </main>
    );

  const gameView = view === 'farm' || view === 'home' || view === 'wardrobe';
  return (
    <div className="app-shell">
      <Hud />
      <AudioSettingsPanel />
      <main className="main-content">
        {gameView && (
          <Suspense fallback={<div className="scene-loading">正在布置场景……</div>}>
            <PhaserGame view={view} />
          </Suspense>
        )}
        {view === 'farm' && <FarmPanel />}
        {view === 'home' && <HomePanel />}
        {view === 'wardrobe' && <WardrobePanel />}
        {view === 'shop' && <ShopPage />}
        {view === 'quests' && <QuestPage />}
        <DevTools />
      </main>
      <TutorialOverlay />
      {notice && (
        <div className="toast" role="status">
          {notice}
        </div>
      )}
      <PwaUpdatePrompt />
      <Navigation />
    </div>
  );
}
